import React, { useState, useEffect } from 'react';
import { getProducts, updateProduct } from '../firebase/services';
import '../styles/CategoryUpdater.css';

const CategoryFixer = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [log, setLog] = useState([]);
  const [categories, setCategories] = useState({});

  // Standard category names (exact spelling and capitalization)
  const standardCategories = {
    'sweets': 'Sweets',
    'namkeen and snacks': 'Namkeen and Snacks',
    'namkeen & snacks': 'Namkeen and Snacks',
    'juice and shake': 'Juice and Shake',
    'juice & shake': 'Juice and Shake',
    'dairy and beverages': 'Dairy and Beverages',
    'dairy & beverages': 'Dairy and Beverages',
    'ice cream and desserts': 'Ice Cream and Desserts',
    'ice cream & desserts': 'Ice Cream and Desserts',
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const productsData = await getProducts();
      setProducts(productsData);
      
      // Group products by category
      const categoryGroups = {};
      productsData.forEach(product => {
        const cat = product.category || 'Uncategorized';
        if (!categoryGroups[cat]) {
          categoryGroups[cat] = [];
        }
        categoryGroups[cat].push(product);
      });
      setCategories(categoryGroups);
    } catch (error) {
      console.error('Error loading products:', error);
      addLog('❌ Error loading products: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const addLog = (message, type = 'info') => {
    setLog(prev => [...prev, { message, type, time: new Date().toLocaleTimeString() }]);
  };

  const normalizeCategory = (category) => {
    if (!category) return 'Uncategorized';
    
    const normalized = category.toLowerCase().trim();
    return standardCategories[normalized] || category;
  };

  const fixCategories = async () => {
    if (!window.confirm('This will standardize all category names. Continue?')) {
      return;
    }

    setUpdating(true);
    setLog([]);
    addLog('🚀 Starting category standardization...', 'info');

    let updatedCount = 0;

    for (const product of products) {
      const currentCategory = product.category || 'Uncategorized';
      const standardCategory = normalizeCategory(currentCategory);

      if (currentCategory !== standardCategory) {
        try {
          await updateProduct(product.id, { category: standardCategory });
          addLog(`✅ UPDATED: ${product.name}`, 'success');
          addLog(`   "${currentCategory}" → "${standardCategory}"`, 'info');
          updatedCount++;
        } catch (error) {
          addLog(`⚠️ Failed to update ${product.name}: ${error.message}`, 'error');
        }
      }
    }

    addLog('\n=== Summary ===', 'info');
    addLog(`✅ Updated: ${updatedCount} products`, 'success');
    addLog('🎉 Done!', 'success');

    setUpdating(false);
    
    // Reload products
    setTimeout(() => {
      loadProducts();
    }, 2000);
  };

  const previewChanges = () => {
    setLog([]);
    addLog('📋 Preview of changes that will be made:', 'info');
    addLog('', 'info');

    let updateCount = 0;

    products.forEach(product => {
      const currentCategory = product.category || 'Uncategorized';
      const standardCategory = normalizeCategory(currentCategory);

      if (currentCategory !== standardCategory) {
        addLog(`✅ WILL UPDATE: ${product.name}`, 'success');
        addLog(`   "${currentCategory}" → "${standardCategory}"`, 'info');
        updateCount++;
      }
    });

    addLog('', 'info');
    addLog(`Total updates needed: ${updateCount}`, 'info');
  };

  if (loading) {
    return <div className="category-updater"><div className="loading">Loading products...</div></div>;
  }

  return (
    <div className="category-updater">
      <h1>Category Fixer Tool</h1>
      <p className="description">
        This tool will standardize all category names to fix duplicates caused by spacing or capitalization differences.
      </p>

      <div className="actions">
        <button onClick={previewChanges} className="btn-preview" disabled={updating}>
          👁️ Preview Changes
        </button>
        <button onClick={fixCategories} className="btn-update" disabled={updating}>
          {updating ? '⏳ Fixing...' : '🔧 Fix Categories'}
        </button>
      </div>

      <div className="stats">
        <div className="stat-card">
          <h3>Total Products</h3>
          <p>{products.length}</p>
        </div>
        <div className="stat-card">
          <h3>Current Categories</h3>
          <p>{Object.keys(categories).length}</p>
        </div>
      </div>

      <div className="changes-list">
        <h2>Current Categories</h2>
        <div className="category-list">
          {Object.entries(categories).map(([category, items]) => (
            <div key={category} className="category-item">
              <div className="category-header">
                <strong>{category}</strong>
                <span className="category-count">{items.length} products</span>
              </div>
              {normalizeCategory(category) !== category && (
                <div className="category-warning">
                  ⚠️ Will be renamed to: <strong>{normalizeCategory(category)}</strong>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {log.length > 0 && (
        <div className="log-container">
          <h2>Activity Log</h2>
          <div className="log">
            {log.map((entry, index) => (
              <div key={index} className={`log-entry log-${entry.type}`}>
                <span className="log-time">[{entry.time}]</span>
                <span className="log-message">{entry.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="changes-list">
        <h2>Standard Category Names</h2>
        <div className="standard-categories">
          <ul>
            {Object.values(standardCategories).filter((v, i, a) => a.indexOf(v) === i).map(cat => (
              <li key={cat}>{cat}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CategoryFixer;
